import { CartGeoCode, CartGeoCodeManagerState, getGeoCodeState, CartMinimalGeoCode } from 'cart-core';

const API_URL = '/api/resource/geo-code';

/**
 * 4th Generation geo code library.
 *
 * Designed to be more decoupled from angular, to use less callbacks, and to use
 * redux-like one way data flows
 */
export default class GeoCodeManager {

    public state: CartGeoCodeManagerState = {
        postalCode: '',
        geoCodes: [],
        selectedGeoCode: null,
        haveGeoCodeOptions: false,
        loading: false,
        postalCodeIsInvalid: false,
    };

    /**
     * @param onStateChangeListener
     *  called each time something changes, this is called with a geoCodeState
     *  object
     * @param XMLHttpRequest just pass the result of "window.XMLHttpRequest" here
     */
    public constructor(
        private onStateChangeListener: (state: CartGeoCodeManagerState) => void,
        private XMLHttpRequest: typeof window.XMLHttpRequest
    ) {
        this.setState = this.setState.bind(this);
        this.handleChange = this.handleChange.bind(this);
    }

    public setState(propertiesToMergeIntoState: Partial<CartGeoCodeManagerState>) {
        this.state = cloneObject(this.state);
        copyOwnPropertiesFrom(this.state, propertiesToMergeIntoState);
        this.onStateChangeListener(this.state);
    }

    private httpGetJson<T>(
        url: string,
        onSuccess: (data: T) => void,
        onFail: () => void
    ) {
        const request = new this.XMLHttpRequest();
        request.onreadystatechange = () => {
            if (request.readyState === 4) {
                if (request.status === 200) {
                    onSuccess(JSON.parse(request.responseText));
                } else {
                    onFail();
                }
            }
        };
        request.open('GET', url, true);
        request.send(null);
    }

    /**
     * @param postalCode the current postal code as entered by the user
     * @param selectedGeoCode optional currently selected geo code model object
     */
    public handleChange (
        postalCode: string,
        selectedGeoCode?: CartMinimalGeoCode
    ) {
        this.setState({postalCode});

        if (!postalCode || postalCode.length === 0) {
            this.setState({geoCodes: [], haveGeoCodeOptions: false, postalCodeIsInvalid: false});
            return;
        } else if (postalCode && postalCode.length !== 5 && postalCode.length !== 10) {
            //5 for normal zips, 10 for "+4" zips
            this.setState({geoCodes: [], haveGeoCodeOptions: false, postalCodeIsInvalid: true});
            return;
        }

        this.setState({loading: true});

        this.httpGetJson(
            API_URL + '?filter%5Bwhere%5D%5Bzip%5D=' + postalCode,
            (geoCodes: CartGeoCode[]) => {
                this.setState({
                    loading: false,
                    ...getGeoCodeState({
                        geoCodes,
                        postalCode,
                        selectedGeoCode,
                    })
                });
            },
            () => {
                this.setState({
                    loading: false,
                    geoCodes: [],
                    haveGeoCodeOptions: false,
                    postalCodeIsInvalid: true,
                });
            }
        );
    };
}

/**
 * Creates a new object with the same prototype and own properties as the given object.
 *
 * Property values and descriptors of the original object will be copied over to
 * the clone.
 */
const cloneObject = <T>(orig: T): T => {
    // 1. copy has same prototype as orig
    const copy = Object.create(Object.getPrototypeOf(orig));

    // 2. copy has all of orig’s properties
    copyOwnPropertiesFrom(copy, orig);

    return copy;
};

/**
 * Copy properties into the target object from the source object
 *
 * This is a mutating (in-place) operation. Property values and descriptors from
 * the source object will be copied directly into the target.
 *
 * @returns The target object
 */
const copyOwnPropertiesFrom = <T, U>(target: T, source: U): T & U => {
    Object.getOwnPropertyNames(source)
        .forEach((propKey) => {
            const desc = Object.getOwnPropertyDescriptor(source, propKey);
            Object.defineProperty(target, propKey, desc);
        });
    return target as (T & U);
};
